/**
 * Canvas renderer for redaction overlays — the SECOND of the editor's two
 * overlay renderers, and the one that produces pixels rather than DOM.
 *
 * Overlay.tsx renders react-rnd boxes while the player is PAUSED; `drawPolys`
 * here paints onto the <canvas> during PLAYBACK (useCollection.ts) and for
 * still-image export (exportRedactedImage.ts). Both draw the same polys, so
 * anything that changes how a poly is laid out has to be applied in both or the
 * editor shows one thing paused and another while playing.
 *
 * That parity is load-bearing for text overlays in particular: their box is a
 * DERIVED value (a function of the text, fontScale, bold, screen-position pins
 * and inset), and the rect stored on the poly can lag behind the settings that
 * produce it. Both renderers therefore re-derive it via computeTextOverlayPoly
 * rather than trusting the stored rect — Overlay.tsx in `hugTextOverlayPoly`,
 * and `drawTextOverlay` below. Drawing the stored rect here instead is what made
 * overlays render correctly when paused but clipped and pinned to the frame edge
 * during playback (VE-30798). One gap remains: `unselectedPolys` are seeded
 * straight into the `otherPolys` accumulator and never reach `drawTextOverlay`,
 * so an unselected text overlay is still outlined at its raw stored rect.
 *
 * Render order is documented inline in `drawPolys` and is meant to match the
 * paused state and, where noted, the backend blur engine.
 */
import {
  BLUR_LEVEL_COEFFS,
  convertPolyToScalable,
  BACKGROUND_BLUR_LEVEL_COEFFS,
  GROUPED_POLY_TYPE,
  REDACTION_CODE_FONT_SCALING_COEFF,
  REDACTION_CODE_MIN_FONT_PX,
  REDACTION_CODE_FONT_SIZE,
  TEXT_OVERLAY_LINE_HEIGHT,
} from '@helpers/constants';
import {
  computeTextOverlayPoly,
  getTextOverlayBackgroundColor,
  makeCanvasTextMeasurer,
  TEXT_OVERLAY_FONT_FAMILY,
} from '@helpers/textOverlay';
import {
  BlurLevel,
  GlobalSettings,
  HighlightedOverlay,
  RedactionType,
  ShapeType,
} from '@common-modules/mediaDetails/models';
import { GroupedBoundingPoly } from '@worker';
import {
  percentagePolyToPixelXYWidthHeight,
  pixelXYWidthHeightToPercentagePoly,
} from './helpers';
import { IndividualRedactionCode } from '@redact-modules/redactionCodes/models';
import { BURNED_IN_TEXT_FONT_FAMILY } from '@common/styles/fonts';
// import { arrayHasContents } from '@common/shared/util';

interface BaseBlurPoly {
  id: string;
  x: number;
  y: number;
  polyWidth: number;
  polyHeight: number;
  shapeType: ShapeType;
  redactionCode?: IndividualRedactionCode;
}

type BlurPoly = BaseBlurPoly & {
  blurPx: number;
};

interface TextOverlayPoly {
  id: string;
  x: number;
  y: number;
  polyWidth: number;
  polyHeight: number;
  textOverlay: NonNullable<GroupedBoundingPoly['textOverlay']>;
}

const outlineStyles: Record<GROUPED_POLY_TYPE, string> = {
  udr: '255, 235, 59',
  head: '0, 160, 119',
  licensePlate: '0, 183, 236',
  laptop: '243, 100, 13',
  poim: '201, 51, 242',
  notepad: '191, 64, 191',
  vehicle: '0, 183, 236',
  card: '18, 116, 236',
  person: '237, 64, 93',
  text_overlay: '217, 93, 70',
};

const disabledStyles = {
  border: 'rgba(200, 200, 200)',
  background: 'rgba(80, 80, 80, 0.15)',
};

const setFill = (
  context: CanvasRenderingContext2D,
  overlayPreview: 'black_fill' | 'outline' | 'redacted',
  fillType: RedactionType,
  type: GROUPED_POLY_TYPE
) =>
  ({
    black_fill: {
      black_fill: () => {
        context.fillStyle = 'black';
      },
      blur: () => {
        context.fillStyle = 'black';
      },
      inverse_blur: () => {
        context.fillStyle = `transparent`;
        context.strokeStyle = 'black';
        context.lineWidth = 2;
      },
      outline: () => {
        context.lineWidth = 3;
        context.strokeStyle = 'white';
        context.fillStyle = 'transparent';
      },
    }[fillType],
    outline: () => {
      if (['inverse_blur', 'outline'].includes(fillType)) {
        context.fillStyle = 'transparent';
      } else {
        context.fillStyle = `rgba(${outlineStyles[type]}, 0.15)`;
      }
      context.strokeStyle = `rgb(${outlineStyles[type]})`;
      context.lineWidth = 2;
    },
    redacted: {
      black_fill: () => {
        context.fillStyle = 'black';
      },
      blur: () => {},
      inverse_blur: () => {},
      outline: () => {
        context.lineWidth = 3;
        context.strokeStyle = 'white';
        context.fillStyle = 'transparent';
      },
    }[fillType],
  })[overlayPreview]();

const drawBoundingBox = (
  context: CanvasRenderingContext2D,
  shapeType: 'rectangle' | 'ellipse',
  x: number,
  y: number,
  polyWidth: number,
  polyHeight: number,
  width: number,
  height: number,
  forward = true
) => {
  if (shapeType === 'rectangle') {
    drawRectangle(context, x, y, polyWidth, polyHeight, forward);
  } else {
    drawEllipse(context, x, y, polyWidth, polyHeight, width, height, forward);
  }
};

const drawRectangle = (
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  polyWidth: number,
  polyHeight: number,
  forward: boolean
) => {
  if (forward) {
    context.rect(x, y, polyWidth, polyHeight);
  } else {
    context.moveTo(x, y);
    context.lineTo(x, y + polyHeight);
    context.lineTo(x + polyWidth, y + polyHeight);
    context.lineTo(x + polyWidth, y);
    context.closePath();
  }
};

const drawEllipse = (
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  polyWidth: number,
  polyHeight: number,
  width: number,
  height: number,
  forward = true
) => {
  context.moveTo(x, y + polyHeight / 2);
  if (forward) {
    if (x <= 0 || y <= 0) {
      context.lineTo(x, y);
      context.lineTo(x + polyWidth / 2, y);
    } else {
      context.bezierCurveTo(x, y + polyHeight / 2, x, y, x + polyWidth / 2, y);
    }
    if (Math.ceil(x) + Math.ceil(polyWidth) >= width || y <= 0) {
      context.lineTo(x + polyWidth, y);
      context.lineTo(x + polyWidth, y + polyHeight / 2);
    } else {
      context.bezierCurveTo(
        x + polyWidth / 2,
        y,
        x + polyWidth,
        y,
        x + polyWidth,
        y + polyHeight / 2
      );
    }
    if (
      Math.ceil(x) + Math.ceil(polyWidth) >= width ||
      Math.ceil(y) + Math.ceil(polyHeight) >= height
    ) {
      context.lineTo(x + polyWidth, y + polyHeight);
      context.lineTo(x + polyWidth / 2, y + polyHeight);
    } else {
      context.bezierCurveTo(
        x + polyWidth,
        y + polyHeight / 2,
        x + polyWidth,
        y + polyHeight,
        x + polyWidth / 2,
        y + polyHeight
      );
    }
    if (x <= 0 || Math.ceil(y) + Math.ceil(polyHeight) >= height) {
      context.lineTo(x, y + polyHeight);
      context.lineTo(x, y + polyHeight / 2);
    } else {
      context.bezierCurveTo(
        x + polyWidth / 2,
        y + polyHeight,
        x,
        y + polyHeight,
        x,
        y + polyHeight / 2
      );
    }
  } else {
    // draw in reverse
    if (x <= 0 || Math.ceil(y) + Math.ceil(polyHeight) >= height) {
      context.lineTo(x, y + polyHeight);
      context.lineTo(x + polyWidth / 2, y + polyHeight);
    } else {
      context.bezierCurveTo(
        x,
        y + polyHeight,
        x + polyWidth / 2,
        y + polyHeight,
        x + polyWidth / 2,
        y + polyHeight
      );
    }
    if (
      Math.ceil(x) + Math.ceil(polyWidth) >= width ||
      Math.ceil(y) + Math.ceil(polyHeight) >= height
    ) {
      context.lineTo(x + polyWidth, y + polyHeight);
      context.lineTo(x + polyWidth, y + polyHeight / 2);
    } else {
      context.bezierCurveTo(
        x + polyWidth,
        y + polyHeight,
        x + polyWidth,
        y + polyHeight / 2,
        x + polyWidth,
        y + polyHeight / 2
      );
    }
    if (Math.ceil(x) + Math.ceil(polyWidth) >= width || y <= 0) {
      context.lineTo(x + polyWidth, y);
      context.lineTo(x + polyWidth / 2, y);
    } else {
      context.bezierCurveTo(
        x + polyWidth,
        y,
        x + polyWidth / 2,
        y,
        x + polyWidth / 2,
        y
      );
    }
    if (x <= 0 || y <= 0) {
      context.lineTo(x, y);
      context.lineTo(x, y + polyHeight / 2);
    } else {
      context.bezierCurveTo(x, y, x, y + polyHeight / 2, x, y + polyHeight / 2);
    }
  }
  context.closePath();
};

const drawRedactionCode = (
  context: CanvasRenderingContext2D,
  redactionCode: GroupedBoundingPoly['redactionCode'],
  x: number,
  y: number,
  polyWidth: number,
  polyHeight: number,
  canvasHeight: number
  // overlayContainerHeight: number | undefined // IMAGE-TODO not entirely sure difference between canvasHeight and overlayContainerHeight
) => {
  if (redactionCode) {
    // calculate font size that ensures text fits within bounding box width
    const calculatedCqw =
      REDACTION_CODE_FONT_SCALING_COEFF / redactionCode.code.length;
    const calculatedPx = (calculatedCqw / 100) * polyWidth;

    const maxFontSize = REDACTION_CODE_FONT_SIZE * canvasHeight;
    // (document.getElementById('overlay-container')?.offsetHeight ?? 1080) // old method of getting canvas height

    const fontSize = Math.min(
      Math.max(
        REDACTION_CODE_MIN_FONT_PX, // min font size to ensure readability
        calculatedPx
      ),
      maxFontSize
    );

    // The constant, not a copy: this is the BURN for a redaction code, and
    // `styles.scss` paints its on-screen preview. If the two ever disagree,
    // the code burned into delivered video stops matching what the reviewer
    // approved.
    context.font = `${fontSize}px ${BURNED_IN_TEXT_FONT_FAMILY}`;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillStyle = redactionCode.codeColor;
    // context.textBaseline = 'alphabetic';

    // Hide overflow text
    context.beginPath();
    context.rect(x, y, polyWidth, polyHeight);
    context.clip();

    const anchorX = x + polyWidth / 2;
    const baselineY = y + polyHeight / 2;

    // TODO if we add support for locations other than center try out this code below

    // context.textAlign = 'left';
    // const code = redactionCode.code;
    // const metrics = context.measureText(code);
    // const ascent =
    //   metrics.actualBoundingBoxAscent ??
    //   metrics.fontBoundingBoxAscent ??
    //   fontSize * 0.72;
    // const descent =
    //   metrics.actualBoundingBoxDescent ??
    //   metrics.fontBoundingBoxDescent ??
    //   fontSize * 0.28;
    // const bbLeft = metrics.actualBoundingBoxLeft ?? 0;
    // const bbRight =
    //   metrics.actualBoundingBoxRight ?? Math.max(metrics.width, 0);

    // const loc = redactionCode.codeLocation ?? 'center';
    // const padX = Math.max(2, fontSize * 0.12);

    // const narrowPoly = polyWidth < fontSize * 4;

    // let anchorX: number;
    // if (loc === 'left') {
    //   anchorX = x + padX;
    // } else if (loc === 'right') {
    //   context.textAlign = 'right';
    //   anchorX = x + polyWidth - padX;
    // } else if (narrowPoly) {
    //   anchorX = x + padX;
    // } else {
    //   anchorX = x + polyWidth / 2 - (bbRight - bbLeft) / 2;
    // }

    // let baselineY = y + polyHeight / 2 + (ascent - descent) / 2;
    // if (loc === 'top') {
    //   baselineY = y + ascent + padX;
    // } else if (loc === 'bottom') {
    //   baselineY = y + polyHeight - descent - padX;
    // }

    context.fillText(redactionCode.code, anchorX, baselineY);
  }
};

const drawTextOverlay = (
  context: CanvasRenderingContext2D,
  textOverlay: NonNullable<GroupedBoundingPoly['textOverlay']>,
  x: number,
  y: number,
  polyWidth: number,
  polyHeight: number,
  referenceWidth: number,
  referenceHeight: number
) => {
  const measure = makeCanvasTextMeasurer(context);
  // Re-derive the box from the overlay's own settings rather than trusting the
  // rect we were handed, exactly as the paused/DOM path does via
  // hugTextOverlayPoly (Overlay.tsx). The stored rect can disagree with the
  // overlay — a quick-placed box is born flush to the frame edge at placeholder
  // size and only gets its hugged geometry persisted later — and drawing
  // against that stale rect clipped the text and pinned the box to the bottom
  // of the frame during playback while the paused view looked correct
  // (VE-30798). Deriving here makes the two renderers agree by construction.
  //
  // computeTextOverlayPoly lays out against the FRAME (referenceWidth/Height),
  // so a line only shrinks when it exceeds the frame and the block is never
  // falsely reported as clipped.
  const { poly: huggedPoly, layout } = computeTextOverlayPoly(
    pixelXYWidthHeightToPercentagePoly(
      { x, y, width: polyWidth, height: polyHeight },
      referenceWidth,
      referenceHeight
    ),
    textOverlay,
    referenceWidth,
    referenceHeight,
    measure
  );
  const { lines, lineFontPx, fontPx, paddingPx, blockWidthPx } = layout;
  if (!lines.some((line) => line.trim().length > 0)) {
    return;
  }
  const {
    x: boxX,
    y: boxY,
    width: boxWidth,
    height: boxHeight,
  } = percentagePolyToPixelXYWidthHeight(
    huggedPoly,
    referenceWidth,
    referenceHeight,
    0
  );

  const lineHeightAt = (index: number) =>
    (lineFontPx[index] ?? fontPx) * TEXT_OVERLAY_LINE_HEIGHT;
  const textBlockHeight = lines.reduce(
    (sum, _line, index) => sum + lineHeightAt(index),
    0
  );
  const anchorX = {
    left: boxX + paddingPx,
    center: boxX + boxWidth / 2,
    right: boxX + boxWidth - paddingPx,
  }[textOverlay.horizontalAlign];

  // The background panel hugs the text block (widest line + symmetric padding
  // on all sides) and is centred vertically in the box; when the box hugs the
  // text this fills the box. The text block is then centred within the panel.
  const panelHeight = Math.min(textBlockHeight + paddingPx * 2, boxHeight);
  const panelY = boxY + Math.max((boxHeight - panelHeight) / 2, 0);
  const textTop = panelY + (panelHeight - textBlockHeight) / 2;

  context.save();
  context.beginPath();
  context.rect(boxX, boxY, boxWidth, boxHeight);
  context.clip();
  context.textAlign = textOverlay.horizontalAlign;
  context.textBaseline = 'alphabetic';

  if (textOverlay.backgroundEnabled) {
    const backgroundX = {
      left: boxX,
      center: boxX + (boxWidth - blockWidthPx) / 2,
      right: boxX + boxWidth - blockWidthPx,
    }[textOverlay.horizontalAlign];
    context.fillStyle = getTextOverlayBackgroundColor(textOverlay);
    context.fillRect(backgroundX, panelY, blockWidthPx, panelHeight);
  }

  context.fillStyle = textOverlay.color;
  let lineY = textTop;
  lines.forEach((line, index) => {
    const lineHeight = lineHeightAt(index);
    const linePx = lineFontPx[index] ?? fontPx;
    context.font = `${textOverlay.bold ? 700 : 400} ${linePx}px ${TEXT_OVERLAY_FONT_FAMILY}`;
    // Centre the actual glyph ink within the line slot (measured ascent/descent
    // beats the "middle" baseline, which centres the taller em box and reads
    // high for caption text with few descenders).
    const metrics = context.measureText(line);
    const ascent = metrics.actualBoundingBoxAscent ?? linePx * 0.7;
    const descent = metrics.actualBoundingBoxDescent ?? linePx * 0.2;
    const slotCenter = lineY + lineHeight / 2;
    const baselineY = slotCenter + (ascent - descent) / 2;
    context.fillText(line, anchorX, baselineY);
    lineY += lineHeight;
  });
  context.restore();
};

const discretize = (polys: BlurPoly[], tolerance: number) => {
  const sortedPolys = polys.sort((a, b) => a.blurPx - b.blurPx);
  if (!sortedPolys[0]) {
    return [];
  }

  let cmin = sortedPolys[0].blurPx;
  let pending: BlurPoly[] = [];
  const clusters: BlurPoly[][] = [];
  for (const s of sortedPolys) {
    const center = (s.blurPx + cmin) / 2;
    const percFromCenter = center / cmin;
    if (percFromCenter - 1 > tolerance) {
      clusters.push(pending);
      pending = [];
      cmin = s.blurPx;
    }
    pending.push(s);
  }
  clusters.push(pending);
  return clusters;
};

export const drawPolys = (
  polys: GroupedBoundingPoly[],
  unselectedPolys: GroupedBoundingPoly[],
  context: CanvasRenderingContext2D,
  overlayPreview: 'black_fill' | 'outline' | 'redacted',
  backgroundBlurLevel: BlurLevel | undefined,
  objectTypeEffects: GlobalSettings['objectTypeEffects'],
  localOverlayBeingUpdated: GroupedBoundingPoly | null,
  highlightedOverlay: HighlightedOverlay | undefined,
  draggedOverlayId: string | null,
  src: HTMLImageElement | HTMLCanvasElement | HTMLVideoElement
) => {
  const { width, height } = context.canvas;
  const blurCanvas = new OffscreenCanvas(width, height);
  const blurContext = blurCanvas.getContext('2d', { alpha: false })!;

  const backgroundBlurPx = Math.floor(
    BACKGROUND_BLUR_LEVEL_COEFFS[backgroundBlurLevel || 0] *
      Math.max(width, height)
  );

  // const overlayContainerHeight =
  //   document.getElementById('overlay-container')?.offsetHeight;

  const unselectedPolysIds = unselectedPolys?.map(({ id }) => id) ?? [];

  // TODO could refactor this file (currently has a lot of duplicate code and different data types !!!)

  /**
   Current order of rendering (should match paused state):
   1. Background blur
   2. inverse blur
   3. inverse blur Redaction codes (eventually this should be moved towards end to match blur engine)
   4. blackfill 
   5. blackfill Redaction codes (eventually this should be moved towards end to match blur engine)
   6. blur 
   7. blurRedaction codes (eventually this should be moved towards end to match blur engine)
   8. outline preview & outline effect
   9. outline preview & outline effect redaction codes
   */

  const [
    blackfillPolys,
    blurPolys,
    inverseBlurPolys,
    otherPolys,
    textOverlayPolys,
  ] = polys.reduce<
    [
      GroupedBoundingPoly[],
      BlurPoly[],
      BaseBlurPoly[],
      GroupedBoundingPoly[],
      TextOverlayPoly[],
    ]
  >(
    (
      [
        blackfillPolys,
        blurPolys,
        inverseBlurPolys,
        otherPolys,
        textOverlayPolys,
      ],
      poly
    ) => {
      const {
        id,
        redactionConfig,
        redactionCode,
        shapeType: polyShapeType,
        textOverlay,
        type,
      } = poly;
      if (localOverlayBeingUpdated?.id === id) {
        return [
          blackfillPolys,
          blurPolys,
          inverseBlurPolys,
          otherPolys,
          textOverlayPolys,
        ];
      }

      const convertedType = convertPolyToScalable(type) ?? 'udr';
      const fillType =
        redactionConfig?.fillType ??
        objectTypeEffects[convertedType].redactionConfig.fillType;

      /* if NOT going to render as blur/inverseBlur ready to add to appropriate list */
      if (
        overlayPreview !== 'redacted' ||
        !['blur', 'inverse_blur'].includes(fillType)
      ) {
        if (textOverlay) {
          // Text overlays render as text only — never as a redaction box, so
          // don't add them to blackfill/other (which would draw an outline).
          const {
            x,
            y,
            width: polyWidth,
            height: polyHeight,
          } = percentagePolyToPixelXYWidthHeight(
            poly.boundingPoly,
            width,
            height,
            convertedType !== 'udr'
              ? objectTypeEffects[convertedType].scaling
              : 0
          );
          textOverlayPolys.push({
            id: poly.id,
            x,
            y,
            polyWidth,
            polyHeight,
            textOverlay,
          });
        } else if (fillType === 'black_fill') {
          blackfillPolys.push(poly);
        } else {
          otherPolys.push(poly); // includes outline preview and outline effect
        }
        return [
          blackfillPolys,
          blurPolys,
          inverseBlurPolys,
          otherPolys,
          textOverlayPolys,
        ];
      }

      // current we package blur / inverse blur polys in a different data structure
      const {
        x,
        y,
        width: polyWidth,
        height: polyHeight,
      } = percentagePolyToPixelXYWidthHeight(
        poly.boundingPoly,
        width,
        height,
        convertedType !== 'udr' ? objectTypeEffects[convertedType].scaling : 0
      );
      const shapeType =
        polyShapeType ??
        (convertedType
          ? objectTypeEffects[convertedType].shapeType
          : 'rectangle');

      if (textOverlay) {
        textOverlayPolys.push({
          id: poly.id,
          x,
          y,
          polyWidth,
          polyHeight,
          textOverlay,
        });
      } else if (fillType === 'inverse_blur') {
        inverseBlurPolys.push({
          id: poly.id,
          x,
          y,
          polyWidth,
          polyHeight,
          shapeType,
          redactionCode,
        });
      } else {
        const blurLevel =
          redactionConfig?.blurLevel ??
          objectTypeEffects[convertedType].redactionConfig.blurLevel;
        const blurPx = Math.floor(
          BLUR_LEVEL_COEFFS[blurLevel] * Math.max(polyWidth, polyHeight)
        );
        blurPolys.push({
          id: poly.id,
          x,
          y,
          blurPx,
          polyWidth,
          polyHeight,
          shapeType,
          redactionCode,
        });
      }
      return [
        blackfillPolys,
        blurPolys,
        inverseBlurPolys,
        otherPolys,
        textOverlayPolys,
      ];
    },
    [[], [], [], [...unselectedPolys], []]
  );

  if (backgroundBlurPx && overlayPreview === 'outline') {
    const { width, height } = context.canvas;

    context.save();
    context.beginPath();

    context.fillStyle = `transparent`;
    context.strokeStyle = `rgb(224, 171, 250)`;
    context.lineWidth = 4;

    drawBoundingBox(context, 'rectangle', 0, 0, width, height, width, height);
    context.fill();
    context.stroke();
    context.restore();
  }

  /* -------------  Draw Background Blur & InverseBlur Polys */

  // if (arrayHasContents(inverseBlurPolys)) {
  if (
    backgroundBlurLevel &&
    backgroundBlurLevel > 0 &&
    backgroundBlurPx &&
    overlayPreview === 'redacted'
  ) {
    const skipRender = false;

    // appears to be working now
    // if (anyOverlap(inverseBlurPolys)) {
    //   // TODO: Implement solution for overlapping inverse blur polys
    //   console.warn(
    //     'Inverse blur polys overlap, this may lead to unexpected results'
    //   );
    //   // skipRender = true;
    // }

    if (!skipRender) {
      // Efficient way to do this is blur entire canvas and clip out the inverse blur polys by drawing them "backwards"
      // This will work as long as the inverse blur polys do not overlap with each other

      // 1. Draw blurred src as background
      context.reset();
      const { width, height } = context.canvas;

      context.filter = `blur(${backgroundBlurPx}px)`;
      context.save();
      context.drawImage(src, 0, 0, width, height);
      context.restore();
      context.filter = 'none';

      // 2. Draw unblurred polys on top
      for (const poly of inverseBlurPolys) {
        const { x, y, polyWidth, polyHeight, shapeType } = poly;
        context.save();
        context.beginPath();
        drawBoundingBox(
          context,
          shapeType,
          x,
          y,
          polyWidth,
          polyHeight,
          width,
          height
        );
        context.clip();
        context.drawImage(src, 0, 0, width, height);
        context.restore();
      }
    }
  }

  /* Draw InverseBlur Poly Redaction Codes */
  for (const {
    polyWidth,
    polyHeight,
    x,
    y,
    redactionCode,
    id,
  } of inverseBlurPolys) {
    context.save();
    context.beginPath();
    if (id !== draggedOverlayId) {
      drawRedactionCode(
        context,
        redactionCode,
        x,
        y,
        polyWidth,
        polyHeight,
        height
        // overlayContainerHeight
      );
    }
    context.restore();
  }

  /* -------------  Draw Blackfill polys - TODO rewrite to reduce code duplication (copied from otherPolys loop ) */

  for (const {
    id,
    boundingPoly,
    redactionCode,
    redactionConfig,
    shapeType: polyShapeType,
    type,
  } of blackfillPolys) {
    const convertedType = convertPolyToScalable(type) ?? 'udr';
    const shapeType =
      polyShapeType ??
      (convertedType
        ? objectTypeEffects[convertedType].shapeType
        : 'rectangle');

    const {
      x,
      y,
      width: polyWidth,
      height: polyHeight,
    } = percentagePolyToPixelXYWidthHeight(
      boundingPoly,
      width,
      height,
      convertedType !== 'udr' ? objectTypeEffects[convertedType].scaling : 0
    );

    context.save();
    context.beginPath();

    const fillType =
      redactionConfig?.fillType ??
      objectTypeEffects[convertedType].redactionConfig.fillType;
    const isUnselected = unselectedPolysIds.includes(id);

    if (isUnselected) {
      context.fillStyle = disabledStyles.background;
      context.strokeStyle = disabledStyles.border;
      context.setLineDash([3, 2]);
      context.lineWidth = 0.5;
    } else {
      setFill(context, overlayPreview, fillType, type);
    }
    if (id !== draggedOverlayId) {
      // render the box if it is not being dragged only
      drawBoundingBox(
        context,
        shapeType,
        x,
        y,
        polyWidth,
        polyHeight,
        width,
        height
      );
      context.fill();
      context.stroke();
      if (!isUnselected) {
        drawRedactionCode(
          context,
          redactionCode,
          x,
          y,
          polyWidth,
          polyHeight,
          height
          // overlayContainerHeight
        );
      }
    }

    context.restore();
  }

  /* -------------  Draw Blurred Polys */

  const discretizedBlurPolys = discretize(blurPolys, 0.3);

  for (const blurGroup of discretizedBlurPolys) {
    // render the box if it is not being dragged only
    const averageBlur =
      blurGroup.reduce((acc, { blurPx }) => acc + blurPx, 0) / blurGroup.length;

    context.save();
    context.beginPath();
    blurContext.filter = `blur(${averageBlur}px)`;
    blurContext.drawImage(context.canvas, 0, 0, width, height);
    for (const { polyWidth, polyHeight, x, y, shapeType, id } of blurGroup) {
      if (id !== draggedOverlayId) {
        drawBoundingBox(
          context,
          shapeType,
          x,
          y,
          polyWidth,
          polyHeight,
          width,
          height
        );
      }
    }
    context.clip();

    context.drawImage(
      blurContext.canvas,
      0,
      0,
      width,
      height,
      0,
      0,
      width,
      height
    );
    context.restore();

    /* Draw Blurred Poly Redaction Codes */
    for (const {
      polyWidth,
      polyHeight,
      x,
      y,
      redactionCode,
      id,
    } of blurGroup) {
      context.save();
      context.beginPath();
      if (id !== draggedOverlayId) {
        drawRedactionCode(
          context,
          redactionCode,
          x,
          y,
          polyWidth,
          polyHeight,
          height
          // overlayContainerHeight
        );
      }
      context.restore();
    }
  }

  /* Draw All Other Polys (outline && outline redaction effect) */
  for (const {
    id,
    boundingPoly,
    redactionCode,
    redactionConfig,
    shapeType: polyShapeType,
    type,
  } of otherPolys) {
    const convertedType = convertPolyToScalable(type) ?? 'udr';
    const shapeType =
      polyShapeType ??
      (convertedType
        ? objectTypeEffects[convertedType].shapeType
        : 'rectangle');

    const {
      x,
      y,
      width: polyWidth,
      height: polyHeight,
    } = percentagePolyToPixelXYWidthHeight(
      boundingPoly,
      width,
      height,
      convertedType !== 'udr' ? objectTypeEffects[convertedType].scaling : 0
    );

    context.save();
    context.beginPath();

    const fillType =
      redactionConfig?.fillType ??
      objectTypeEffects[convertedType].redactionConfig.fillType;
    const isUnselected = unselectedPolysIds.includes(id);

    if (isUnselected) {
      context.fillStyle = disabledStyles.background;
      context.strokeStyle = disabledStyles.border;
      context.setLineDash([3, 2]);
      context.lineWidth = 0.5;
    } else {
      setFill(context, overlayPreview, fillType, type);
    }
    if (id !== draggedOverlayId) {
      // render the box if it is not being dragged only
      drawBoundingBox(
        context,
        shapeType,
        x,
        y,
        polyWidth,
        polyHeight,
        width,
        height
      );
      const redactedOutlineStrokeOnly =
        overlayPreview === 'redacted' &&
        fillType === 'outline' &&
        !isUnselected;
      if (!redactedOutlineStrokeOnly) {
        context.fill();
      }
      context.stroke();
      if (!isUnselected) {
        drawRedactionCode(
          context,
          redactionCode,
          x,
          y,
          polyWidth,
          polyHeight,
          height
          // overlayContainerHeight
        );
      }
    }

    context.restore();
  }

  for (const {
    id,
    polyWidth,
    polyHeight,
    textOverlay,
    x,
    y,
  } of textOverlayPolys) {
    if (id !== draggedOverlayId) {
      drawTextOverlay(
        context,
        textOverlay,
        x,
        y,
        polyWidth,
        polyHeight,
        width,
        height
      );
    }
  }

  /* Add resize markers indicate highlightedOverlay */
  if (highlightedOverlay) {
    const highlightedPoly = polys.find(
      (poly) =>
        !!highlightedOverlay.groupId &&
        poly.groupId === highlightedOverlay.groupId &&
        localOverlayBeingUpdated?.id !== poly.id
    );

    if (highlightedPoly) {
      const convertedType =
        convertPolyToScalable(highlightedPoly.type) ?? 'udr';

      const size = percentagePolyToPixelXYWidthHeight(
        highlightedPoly.boundingPoly,
        width,
        height,
        convertedType !== 'udr' ? objectTypeEffects[convertedType].scaling : 0
      );
      context.save();
      context.beginPath();
      context.fillStyle = 'white';
      const markerSize = 6;

      const { width: polyWidth, height: polyHeight } = size;

      const x = size.x - markerSize / 2; // center align
      const y = size.y - markerSize / 2; // center align

      context.rect(x, y, markerSize, markerSize); // upper left
      context.rect(x + polyWidth, y, markerSize, markerSize); // upper right
      context.rect(x + polyWidth, y + polyHeight, markerSize, markerSize); // lower right
      context.rect(x, y + polyHeight, markerSize, markerSize); // lower left

      context.rect(x + polyWidth / 2, y, markerSize, markerSize); // top
      context.rect(x + polyWidth, y + polyHeight / 2, markerSize, markerSize); // right
      context.rect(x, y + polyHeight / 2, markerSize, markerSize); // left
      context.rect(x + polyWidth / 2, y + polyHeight, markerSize, markerSize); // bottom
      context.fill();
      context.restore();
    }
  }
};

/** DONT NEED THESE CURRENTLY */

// /**
//  * Check if any polygons overlap.
//  * @param polys - Array of polygons to check for overlap.
//  * @returns True if any polygons overlap, false otherwise.
//  */
// function anyOverlap(polys: BaseBlurPoly[]) {
//   for (let i = 0; i < polys.length; i++) {
//     for (let j = i + 1; j < polys.length; j++) {
//       if (polyOverlap(polys[i]!, polys[j]!)) {
//         return true;
//       }
//     }
//   }
//   return false;
// }

// /**
//  * Check if two polygons overlap.
//  * @param poly1 - First polygon.
//  * @param poly2 - Second polygon.
//  * @returns True if the polygons overlap, false otherwise.
//  */
// function polyOverlap(poly1: BaseBlurPoly, poly2: BaseBlurPoly) {
//   // This doesn't take the poly shape into account and just assumes rectangles. It is possible to do a more complex check for ellipses but may not be worth the complexity.
//   return (
//     poly1.x < poly2.x + poly2.polyWidth &&
//     poly1.x + poly1.polyWidth > poly2.x &&
//     poly1.y < poly2.y + poly2.polyHeight &&
//     poly1.y + poly1.polyHeight > poly2.y
//   );
// }
